import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  Activity,
  ChartNoAxesCombined,
  CheckCircle2,
  Circle,
  Clock3,
  Layers,
  ShieldAlert,
  TrendingUp,
} from 'lucide-react'
import {
  Card,
  ErrorState,
  MoneyValue,
  SelectField,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import { useCustomerSuccess } from '../api/customer-success.api'
import type { CustomerSuccessMetrics } from '../api/customer-success.api'
import * as styles from './AdminCustomerSuccessPanel.css'

const riskLabels = {
  LOW: { label: 'Baixo', tone: 'success' },
  MEDIUM: { label: 'Médio', tone: 'awaiting' },
  HIGH: { label: 'Alto', tone: 'closed' },
  UNKNOWN: { label: 'Dados insuficientes', tone: 'neutral' },
} as const
const reasons: Record<CustomerSuccessMetrics['churnRisk']['reason'], string> = {
  SALES_BLOCKED: 'A conta está com as vendas bloqueadas.',
  NO_FIRST_VALUE: 'A conta tem pelo menos 14 dias e ainda não confirmou uma venda.',
  INACTIVE_30_DAYS: 'Nenhum login ou pagamento confirmado nos últimos 30 dias.',
  INACTIVE_14_DAYS: 'Nenhum login ou pagamento confirmado nos últimos 14 dias.',
  REVENUE_DROP_50: 'O bruto caiu 50% ou mais em relação ao período anterior, nesta moeda.',
  REVENUE_DROP_20: 'O bruto caiu entre 20% e 50% em relação ao período anterior, nesta moeda.',
  LOW_ADOPTION: 'Menos da metade dos recursos essenciais foi adotada.',
  ONBOARDING: 'Conta em início de uso, ainda sem primeira venda confirmada.',
  INSUFFICIENT_ACTIVITY: 'O histórico disponível não permite avaliar a atividade recente.',
  REGULAR_SIGNALS: 'Os sinais disponíveis não atingiram os limites de atenção.',
}
const features: Record<CustomerSuccessMetrics['adoption']['features'][number]['key'], string> = {
  RECEIVING_VERIFIED: 'Cadastro de recebimento aprovado',
  SALES_CHANNEL: 'Produto próprio ou parceria com oferta e checkout ativos',
  CONFIRMED_SALE: 'Primeira venda confirmada',
}
const number = (value: number) => value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
const elapsed = (days: number) => `${number(days)} ${days === 1 ? 'dia' : 'dias'}`
const date = (value: string) =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(value))

export function AdminCustomerSuccessPanel({
  userId,
  canReadRevenue,
}: {
  userId: string
  canReadRevenue: boolean
}) {
  const [days, setDays] = useState(30)
  const currency = 'BRL'
  const query = useCustomerSuccess(userId, days, currency, canReadRevenue)
  if (!canReadRevenue)
    return (
      <p className={styles.description}>
        Seu perfil precisa da permissão de receita para consultar as métricas de Customer Success.
      </p>
    )

  return (
    <section className={styles.section} aria-labelledby="customer-success-title">
      <div className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title} id="customer-success-title">
            Customer Success
          </h2>
          <p className={styles.description}>
            Faturamento confirmado, ativação e sinais de acompanhamento da conta.
          </p>
        </div>
        <div className={styles.filters}>
          <SelectField
            label="Período de faturamento"
            value={String(days)}
            onChange={(event) => setDays(Number(event.target.value))}
            options={[7, 30, 60, 90, 180, 365].map((value) => ({
              value: String(value),
              label: `Últimos ${value} dias`,
            }))}
          />
        </div>
      </div>
      <p className={styles.description}>
        O período inclui hoje, no horário de Brasília. GMV e variação seguem a mesma fonte do painel
        seller, sem conversão entre moedas. Ativação, adoção e atividade consideram toda a conta.
      </p>
      {query.isPending ? (
        <div role="status" aria-label="Carregando métricas de Customer Success">
          <Skeleton height={300} />
        </div>
      ) : query.isError ? (
        <ErrorState
          title="Não foi possível carregar as métricas"
          onRetry={() => void query.refetch()}
        />
      ) : (
        <Metrics metrics={query.data} />
      )}
    </section>
  )
}

function Metric({
  title,
  icon,
  value,
  children,
}: {
  title: string
  icon: ReactNode
  value: ReactNode
  children: ReactNode
}) {
  return (
    <Card padding="md">
      <article className={styles.metric} aria-label={title}>
        <div className={styles.label}>
          {icon}
          <h3 className={styles.metricTitle}>{title}</h3>
        </div>
        <div className={styles.value}>{value}</div>
        <div className={styles.description}>{children}</div>
      </article>
    </Card>
  )
}

function Metrics({ metrics: data }: { metrics: CustomerSuccessMetrics }) {
  const risk = riskLabels[data.churnRisk.level]
  const firstValue = data.timeToFirstValue
  const activity = data.lastActivity
  return (
    <>
      <div className={styles.grid}>
        <Metric
          title="GMV no período"
          icon={<ChartNoAxesCombined size={18} />}
          value={<MoneyValue {...data.gmv.current} size="lg" />}
        >
          <p>
            {data.gmv.paidCharges} cobranças pagas em {data.currency}, incluindo renovações, antes
            de taxas e ajustes.
          </p>
          <p>
            Período anterior: <MoneyValue {...data.gmv.previous} size="sm" />
          </p>
        </Metric>
        <Metric
          title="Variação de faturamento"
          icon={<TrendingUp size={18} />}
          value={
            data.revenueChange.percentage === null
              ? 'Sem base'
              : `${data.revenueChange.percentage > 0 ? '+' : ''}${number(data.revenueChange.percentage)}%`
          }
        >
          {data.revenueChange.status === 'INSUFFICIENT_HISTORY'
            ? 'A conta ainda não tinha todo o período anterior de histórico.'
            : data.revenueChange.status === 'NO_BASE'
              ? 'Não houve faturamento em reais no período anterior.'
              : `Comparação com os ${data.window.days} dias anteriores, sem sobreposição.`}
        </Metric>
        <Metric
          title="Tempo até o primeiro valor"
          icon={<Clock3 size={18} />}
          value={
            firstValue.days === null
              ? firstValue.status === 'NOT_REACHED'
                ? 'Ainda sem venda'
                : 'Revisar histórico'
              : elapsed(firstValue.days)
          }
        >
          {firstValue.days === null
            ? 'Dias entre o cadastro e a primeira venda com pagamento confirmado.'
            : `Do cadastro ao primeiro pagamento confirmado, em ${date(firstValue.firstPaidAt!)}.`}
        </Metric>
        <Metric
          title="Adoção de recursos essenciais"
          icon={<Layers size={18} />}
          value={`${number(data.adoption.percentage)}%`}
        >
          <p>
            {data.adoption.used} de {data.adoption.total} recursos, por configuração atual e uso
            comprovado.
          </p>
          <details>
            <summary className={styles.disclosure}>Ver recursos considerados</summary>
            <ul className={styles.featureList}>
              {data.adoption.features.map((feature) => (
                <li key={feature.key} className={styles.feature}>
                  {feature.used ? (
                    <CheckCircle2 size={16} aria-hidden="true" />
                  ) : (
                    <Circle size={16} aria-hidden="true" />
                  )}
                  <span>
                    {features[feature.key]}:{' '}
                    <strong>{feature.used ? 'Adotado' : 'Pendente'}</strong>
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </Metric>
        <Metric
          title="Última atividade"
          icon={<Activity size={18} />}
          value={
            activity.days === null
              ? 'Sem registro'
              : activity.days === 0
                ? 'Hoje'
                : `Há ${elapsed(activity.days)}`
          }
        >
          <p>
            {activity.at
              ? `${activity.source === 'LOGIN' ? 'Login' : 'Pagamento confirmado'} em ${date(activity.at)}.`
              : 'Nenhum login ou pagamento confirmado registrado.'}
          </p>
          {!activity.loginObserved ? (
            <p>Logins anteriores ao início do registro não estão disponíveis.</p>
          ) : null}
        </Metric>
        <Metric
          title="Risco de churn"
          icon={<ShieldAlert size={18} />}
          value={<StatusBadge label={risk.label} tone={risk.tone} />}
        >
          <p>{reasons[data.churnRisk.reason]}</p>
          <p>Regra inicial de acompanhamento. Não representa probabilidade de cancelamento.</p>
        </Metric>
      </div>
      <p className={styles.description}>
        Atualizado em {date(data.window.asOf)} (Brasília). Regra {data.ruleVersion}. GMV é volume
        comercial e não representa saldo Stripe nem pagamento bancário.
      </p>
    </>
  )
}
